import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  type RefObject
} from 'react';
import { Box, useTheme } from '@mui/material';
import { ToolOption } from './WebNavSidebar';
import CRMMapping from './CRMMapping';
import SchedulingToolPanel from './SchedulingToolPanel';
import ProspectClassificationToolPanel from './ProspectClassificationToolPanel';
import TargetPersonaToolPanel from './TargetPersonaToolPanel';
import CampaignSettings from './CampaignSettings';
import AIAssistantPanel from './AIAssistantPanel';
import ColumnEditorPanel, { ColumnEditorInfo } from './ColumnEditorPanel';
import type { SpreadsheetRefHandle } from '../spreadsheet/spreadsheetTypes';

interface ToolPanelProps {
  isOpen: boolean;
  selectedTool: ToolOption | null;
  columnEditorColumn: ColumnEditorInfo | null;
  /** When true, enrichment or prioritization locks the spreadsheet. */
  spreadsheetJobLocked?: boolean;
  /** When true, enrichment only — for messages that refer to enrichment specifically. */
  spreadsheetLockedDuringEnrichment?: boolean;
  onCloseToolPanel: () => void;
  onColumnUpdated?: (updates: Partial<ColumnEditorInfo>) => void;
  /** When AI GTM engineer finishes a turn, refresh spreadsheet from backend. */
  onGtmEngineerComplete?: () => void;
  /** Row indices currently hidden by the sheet filter; CRM sync will exclude these rows. */
  getFilteredOutRowIds?: () => number[] | null;
  getEnrichmentSelection?: () => {
    row_ids: number[];
    column_names: string[];
  } | null;
  isProspectPrioritizationActive?: boolean;
  spreadsheetRef?: RefObject<SpreadsheetRefHandle | null>;
  /** When true with AI GTM open, the tool panel grows to fill the area left of the (hidden) spreadsheet. */
  aiGtmEngineerExpanded?: boolean;
  onAiGtmEngineerExpandedChange?: (expanded: boolean) => void;
}

const MIN_WIDTH = 300;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 400;
const STORAGE_KEY = 'toolPanelWidth';

const ToolPanel: React.FC<ToolPanelProps> = ({
  isOpen,
  selectedTool,
  columnEditorColumn,
  spreadsheetJobLocked,
  spreadsheetLockedDuringEnrichment = false,
  onCloseToolPanel,
  onColumnUpdated,
  onGtmEngineerComplete,
  getFilteredOutRowIds,
  getEnrichmentSelection,
  isProspectPrioritizationActive = false,
  spreadsheetRef,
  aiGtmEngineerExpanded = false,
  onAiGtmEngineerExpandedChange
}) => {
  const theme = useTheme();

  const [width, setWidth] = useState(() => {
    const savedWidth = localStorage.getItem(STORAGE_KEY);
    return savedWidth ? parseInt(savedWidth, 10) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // Save width to localStorage when it changes
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem(STORAGE_KEY, width.toString());
    }
  }, [width, isOpen]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!panelRef.current) return;

      const rect = panelRef.current.getBoundingClientRect();
      // Calculate width based on mouse position relative to panel's left edge
      const newWidth = e.clientX - rect.left;
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    // Prevent text selection while resizing
    const handleSelectStart = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectstart', handleSelectStart);

    // Change cursor for entire document while resizing
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectstart', handleSelectStart);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const effectiveTool =
    selectedTool ??
    (columnEditorColumn ? ToolOption.COLUMN_EDITOR : null);

  const gtmFillsMainArea =
    aiGtmEngineerExpanded && effectiveTool === ToolOption.AI_ASSISTANT;

  const renderToolContent = () => {
    // Resolve race: isPanelOpen can be true (from columnEditorColumn) before
    // selectedTool is updated in SideBar's useEffect. `effectiveTool` is inferred above.
    switch (effectiveTool) {
      case ToolOption.COLUMN_EDITOR:
        if (!columnEditorColumn) return null;
        return (
          <ColumnEditorPanel
            key={columnEditorColumn.columnIndex}
            column={columnEditorColumn}
            onClose={onCloseToolPanel}
            onColumnUpdated={onColumnUpdated}
          />
        );
      case ToolOption.AI_ASSISTANT:
        return (
          <AIAssistantPanel
            isExpanded={aiGtmEngineerExpanded}
            onExpandedChange={onAiGtmEngineerExpandedChange}
            onClose={onCloseToolPanel}
            onGtmEngineerComplete={onGtmEngineerComplete}
          />
        );
      case ToolOption.SCHEDULING:
        return (
          <SchedulingToolPanel
            onClose={onCloseToolPanel}
            getEnrichmentSelection={getEnrichmentSelection}
          />
        );
      case ToolOption.CRM_SYNC:
        return (
          <CRMMapping
            onClose={onCloseToolPanel}
            getFilteredOutRowIds={getFilteredOutRowIds}
          />
        );
      case ToolOption.TARGET_PERSONA:
        return (
          <TargetPersonaToolPanel
            onClose={onCloseToolPanel}
            getFilteredOutRowIds={getFilteredOutRowIds}
            spreadsheetJobLocked={spreadsheetJobLocked}
            spreadsheetLockedDuringEnrichment={
              spreadsheetLockedDuringEnrichment
            }
          />
        );
      case ToolOption.PROSPECT_PRIORITIZATION:
        if (!spreadsheetRef) return null;
        return (
          <ProspectClassificationToolPanel
            onClose={onCloseToolPanel}
            spreadsheetRef={spreadsheetRef}
            isProspectPrioritizationActive={isProspectPrioritizationActive}
            getFilteredOutRowIds={getFilteredOutRowIds}
            spreadsheetJobLocked={spreadsheetJobLocked}
            spreadsheetLockedDuringEnrichment={
              spreadsheetLockedDuringEnrichment
            }
          />
        );
      case ToolOption.SETTINGS:
        return <CampaignSettings onClose={onCloseToolPanel} />;
      default:
        return null;
    }
  };

  return (
    <Box
      ref={panelRef}
      sx={{
        width: gtmFillsMainArea
          ? '100%'
          : isOpen
            ? `${width}px`
            : '0px',
        minWidth: gtmFillsMainArea
          ? 0
          : isOpen
            ? `${width}px`
            : '0px',
        maxWidth: gtmFillsMainArea ? 'none' : undefined,
        flex: gtmFillsMainArea ? 1 : undefined,
        minHeight: 0,
        height: '100%',
        backgroundColor: theme.customColors.themeSecondary,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: gtmFillsMainArea ? 1 : 0,
        transition: isResizing
          ? 'none'
          : gtmFillsMainArea
            ? 'none'
            : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        borderRadius: '15px 15px 0 0',
        mx: isOpen ? 1 : 0,
        position: 'relative'
      }}
    >
      {/* Resize Handle */}
      {isOpen && !gtmFillsMainArea && (
        <Box
          ref={resizeHandleRef}
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            zIndex: 10,
            '&:hover': {
              backgroundColor: theme.customColors.themePrimary || '#1976d2'
            },
            ...(isResizing && {
              backgroundColor: theme.customColors.themePrimary || '#1976d2'
            })
          }}
        />
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          py: 1,
          px: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {renderToolContent()}
      </Box>
    </Box>
  );
};

export default ToolPanel;
